import { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MotifIcon,
  MotifProgressLoader,
  MotifErrorMessage,
} from "@ey-xd/motif-react";
import { IconoirArrowLeft } from "@ey-xd/motif-icon";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import PageTitle from "@/components/Form/PageTitle/PageTitle";
import Table from "@/components/Table/table";
import { CreditorColumnDefs } from "./creditorTable/CreditorColumnDefinations";
import TitleComplement from "./creditorTable/TitleComplement";
import {
  useLazyGetPreviewBasicSearchCreditorListApiDataQuery,
  useLazySearchCreditorListDataQuery,
} from "@/services/creditors/creditorsReducers";
import { useLazyGetPreviewSchemeQuery } from "@/services/scheme/schemeReducer";
import { BasicSearch } from "@/features/creditor/creditorsTab/basicSearch";
import Claim from "./claim/Claim";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { useCase } from "@/features/case/use-case";
import { useDispatch, useSelector } from "react-redux";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
const SchemePreview = () => {
  const { t } = useTranslation();
  const { caseId, schemeId } = useParams();
  const caseData = useCase(caseId, true);
  const retainState = useSelector(
    (e: any) => e.retainState.schemePreviewTabState
  );
  const dispatch = useDispatch();
  const [showBasicSearch, setShowBasicSearch] = useState(false);
  const clearSearch = retainState.clearSearch;
  const [searchQuery, setSearchQuery] = useState(retainState.SearchText || "");
  const isSearchActive = retainState.isSearchActive;
  const [isPreviewScheme, setIsPreviewScheme] = useState(
    retainState.isPreviewScheme || false
  );
  let { schemeVersionId } = useParams();
  const location = useLocation();
  const schemeVersion: any = schemeVersionId || location.state?.schemeVersion;
  const [creditorId, setCreditorId] = useState(
    retainState.selectedCreditorId || ""
  );
  const [formValues, setFormValues] = useState<any>(
    retainState.formValues || {
      claimId: "",
      creditorId: "",
      creditorName: "",
      debtorName: "",
      country: "",
      province: "",
      postalCode: "",
      phoneNumber: "",
      claimUpdateDate: undefined,
    }
  );
  const [
    triggerPreviewScheme,
    { isLoading: isPreviewLoading, data: previewData, error: previewError },
  ] = useLazyGetPreviewSchemeQuery();
  const [
    triggerSearch,
    {
      data: searchData,
      isError,
      isFetching: searchFetching,
      status: searchStatus,
    },
  ] = useLazySearchCreditorListDataQuery();
  const [triggerBasicSearch, { data, error, isFetching, status }] =
    useLazyGetPreviewBasicSearchCreditorListApiDataQuery();
  let tableLoadingData = customTableLoadingData;
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const paginationObj = useMemo(() => retainState, [retainState]);
  const loadData = useCallback(
    (values: any) => {
      let args = {
        ...retainState,
        ...values,
        ...{ CaseID: caseId, caseId: caseId },
      };
      let value: any = {
        PageNumber: args?.PageNumber || 1,
        PageSize: args?.PageSize,
        SearchText: args?.SearchText || "",
      };
      if (args?.SortOrder && args?.SortColumn) {
        value.SortOrder = args?.SortOrder;
        value.SortColumn = args?.SortColumn;
      }
      if (args.isSearchActive) {
        triggerSearch({
          ...value,
          CaseID: caseId,
        });
      } else {
        triggerBasicSearch({
          ...value,
          ...args.formValues,
          caseId: caseId,
        });
      }
    },
    [caseId, triggerBasicSearch, triggerSearch, retainState]
  );
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "schemePreviewTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
    loadData({
      SortOrder: data.SortOrder,
      SortColumn: data.SortColumn,
      sortedColumnTypes: columnTypesData,
    });
  }
  let resetSortStatus = useCallback(() => {
    let json: any = { ...retainState.sortedColumnTypes };
    for (const key in json) {
      json[key] = { sort: null };
    }
    return json;
  }, [retainState.sortedColumnTypes]);
  console.log(retainState, "retainState");
  useEffect(() => {
    if (retainState.schemeVersion === schemeVersion) {
      loadData(paginationObj);
      if (retainState.schemeVersion && retainState.selectedCreditorId) {
        triggerPreviewScheme({
          schemeVersion: retainState.schemeVersion,
          creditorId: retainState.selectedCreditorId,
        })
          .unwrap()
          .catch((error) => {
            console.error("Preview Scheme failed:", error);
          });
      }
    } else {
      setSearchQuery("");
      setCreditorId("");
      setIsPreviewScheme(false);
      dispatch(
        setComponentState1({
          entityName: "schemePreviewTabState",
          entityState: {
            PageNumber: 1,
            SearchText: "",
            SortOrder: "",
            SortColumn: "",
            PageSize: paginationPageSize,
            sortedColumnTypes: { ...resetSortStatus() },
            formValues: null,
            clearSearch: false,
            selectedCreditorId: "",
            isSearchActive: false,
            schemeVersion: schemeVersion,
            isPreviewScheme: false,
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retainState.schemeVersion]);

  const breadcrumbItems = [
    {
      label: "Back",
      elementKey: "Back",
      text: t("global.globalActions.back"),
      url: `/case/${caseId}/scheme-manage/${schemeId}`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
  ];

  function clearBasicSearch() {
    setSearchQuery("");
    setShowBasicSearch(false);
    setFormValues((prevValues: any) => ({
      ...prevValues,
      claimId: "",
      creditorId: "",
      creditorName: "",
      debtorName: "",
      country: "",
      province: "",
      postalCode: "",
      phoneNumber: "",
      claimUpdateDate: undefined,
    }));
    dispatch(
      setComponentState1({
        entityName: "schemePreviewTabState",
        entityState: {
          PageNumber: 1,
          SearchText: "",
          SortOrder: "",
          SortColumn: "",
          PageSize: paginationPageSize,
          sortedColumnTypes: { ...resetSortStatus() },
          isSearchActive: false,
          clearSearch: false,
          formValues: null,
        },
      })
    );
    loadData({
      PageNumber: 1,
      SearchText: "",
      SortOrder: "",
      SortColumn: "",
      PageSize: paginationPageSize,
      sortedColumnTypes: { ...resetSortStatus() },
      isSearchActive: false,
      clearSearch: false,
      formValues: null,
    });
  }

  const handleChange = (name: any, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  //Submit the form value in URL
  function handleSubmit() {
    let formData = {
      ...formValues,
      caseId: caseId,
    };
    if (formValues?.claimUpdateDate) {
      // Create a Date object
      const date = new Date(formValues?.claimUpdateDate);

      // Extract day, month, and year
      const day = ("0" + date.getDate()).slice(-2); // Ensure two digits
      const month = ("0" + (date.getMonth() + 1)).slice(-2); // Ensure two digits, month is 0-indexed
      const year = date.getFullYear();

      // Format in dd-mm-yyyy
      const updateDate = `${year}-${month}-${day}`;

      formData.updateDate = updateDate;
    }

    dispatch(
      setComponentState1({
        entityName: "schemePreviewTabState",
        entityState: {
          PageNumber: 1,
          SearchText: "",
          SortOrder: "",
          SortColumn: "",
          PageSize: paginationPageSize,
          sortedColumnTypes: { ...resetSortStatus() },
          clearSearch: true,
          formValues: { ...formData },
        },
      })
    );
    loadData({
      PageNumber: 1,
      SearchText: "",
      SortOrder: "",
      SortColumn: "",
      PageSize: paginationPageSize,
      sortedColumnTypes: { ...resetSortStatus() },
      clearSearch: true,
      formValues: { ...formData },
    });
    setSearchQuery("");
  }
  const handleQuickSearchClear = useCallback(() => {
    setSearchQuery("");

    dispatch(
      setComponentState1({
        entityName: "schemePreviewTabState",
        entityState: {
          PageNumber: 1,
          SearchText: "",
          SortOrder: "",
          SortColumn: "",
          PageSize: paginationPageSize,
          sortedColumnTypes: { ...resetSortStatus() },
          isSearchActive: false,
          clearSearch: false,
        },
      })
    );
    loadData({
      PageNumber: 1,
      SearchText: "",
      SortOrder: "",
      SortColumn: "",
      PageSize: paginationPageSize,
      sortedColumnTypes: { ...resetSortStatus() },
      isSearchActive: false,
      clearSearch: false,
    });
  }, [paginationPageSize, resetSortStatus, loadData, dispatch]);

  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchQuery(searchValue);
      if (searchValue.trim() === "") {
        setFormValues((prevValues: any) => ({
          ...prevValues,
          claimId: "",
          creditorId: "",
          creditorName: "",
          debtorName: "",
          country: "",
          province: "",
          postalCode: "",
          phoneNumber: "",
          claimUpdateDate: undefined,
        }));
        dispatch(
          setComponentState1({
            entityName: "schemePreviewTabState",
            entityState: {
              PageNumber: 1,
              SearchText: "",
              SortOrder: "",
              SortColumn: "",
              PageSize: paginationPageSize,
              sortedColumnTypes: { ...resetSortStatus() },
              isSearchActive: false,
              clearSearch: false,
              formValues: null,
            },
          })
        );
        loadData({
          PageNumber: 1,
          SearchText: "",
          SortOrder: "",
          SortColumn: "",
          PageSize: paginationPageSize,
          sortedColumnTypes: { ...resetSortStatus() },
          isSearchActive: false,
          clearSearch: false,
          formValues: null,
        });
      } else {
        setFormValues((prevValues: any) => ({
          ...prevValues,
          claimId: "",
          creditorId: "",
          creditorName: "",
          debtorName: "",
          country: "",
          province: "",
          postalCode: "",
          phoneNumber: "",
          claimUpdateDate: undefined,
        }));
        dispatch(
          setComponentState1({
            entityName: "schemePreviewTabState",
            entityState: {
              PageNumber: 1,
              SearchText: searchValue,
              SortOrder: "",
              SortColumn: "",
              PageSize: paginationPageSize,
              sortedColumnTypes: { ...resetSortStatus() },
              isSearchActive: true,
              clearSearch: false,
              formValues: null,
            },
          })
        );
        loadData({
          PageNumber: 1,
          SearchText: searchValue,
          SortOrder: "",
          SortColumn: "",
          PageSize: paginationPageSize,
          sortedColumnTypes: { ...resetSortStatus() },
          isSearchActive: true,
          clearSearch: false,
          formValues: null,
        });
      }
    },
    [paginationPageSize, resetSortStatus, loadData, dispatch]
  );

  const handlePreviewSearch = () => {
    triggerPreviewScheme({ schemeVersion, creditorId });
    dispatch(
      setComponentState1({
        entityName: "schemePreviewTabState",
        entityState: {
          schemeVersion: schemeVersion,
          selectedCreditorId: creditorId,
          isPreviewScheme: true,
        },
      })
    );
  };

  const tableData = isSearchActive
    ? searchData?.results
    : data?.results
    ? data?.results
    : [];
  const tableDataFetching = searchFetching || isFetching;
  const tableDataError = error || isError;
  const hasMoreRecords = isSearchActive
    ? searchData?.hasMoreRecords
    : data?.hasMoreRecords;
  const apiRes = isSearchActive ? searchData : data;
  const apiStatus = isSearchActive ? searchStatus : status;
  return (
    <>
      <Breadcrumb items={breadcrumbItems} ariaLabel="Breadcrumb navigation" />
      <PageTitle
        title={"pages.configuration.scheme.previewTitle"}
        description={"pages.configuration.scheme.previewDescription"}
      />
      <div className="ccwp_table_wrapper ccwp_table_dropdown_wrapper">
        <div
          className={
            "ccwp_claim_claimlisttable" +
            (clearSearch ? " ccwp_creditor_header" : "") +
            (showBasicSearch ? " hide_claims_table" : "")
          }
        >
          <Table
            columnDefs={CreditorColumnDefs({
              creditorId,
              setCreditorId,
              setIsPreviewScheme,
              caseData,
            })}
            columnTypes={{ ...retainState.sortedColumnTypes }}
            handlePaginationData={handlePaginationData}
            paginationObj={paginationObj}
            cellRenderers={tableDataFetching ? [] : tableData}
            TitleComplement={() => (
              <TitleComplement
                showBasicSearch={showBasicSearch}
                setShowBasicSearch={setShowBasicSearch}
                clearBasicSearch={clearBasicSearch}
                clearSearch={clearSearch}
                onSearch={handleSearch}
                handleQuickSearchClear={handleQuickSearchClear}
                searchQuery={searchQuery}
                isPreviewScheme={isPreviewScheme}
                handlePreviewSearch={handlePreviewSearch}
              />
            )}
            title={"pages.configuration.scheme.previewTableTitle"}
            description={"pages.configuration.scheme.previewTableDescription"}
            pagination={false}
            overlayNoRowsTemplate={
              tableDataFetching
                ? t(tableLoadingData)
                : tableDataError
                ? t("global.globalNotifications.wentWrong")
                : t("global.globalNotifications.noRecords")
            }
          />
          {!showBasicSearch && (
            <CustomPagination
              apiData={apiRes}
              apiStatus={apiStatus}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "schemePreviewTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
                loadData({
                  PageSize: page,
                  PageNumber: 1,
                });
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "schemePreviewTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
                loadData({
                  PageNumber: paginationObj.PageNumber + 1,
                });
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "schemePreviewTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
                loadData({
                  PageNumber: paginationObj.PageNumber - 1,
                });
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          )}

          {showBasicSearch && (
            <BasicSearch
              handleChange={handleChange}
              creditorName={formValues.creditorName}
              creditorId={formValues.creditorId}
              claimId={formValues.claimId}
              debtorName={formValues.debtorName}
              country={formValues.country}
              province={formValues.province}
              postalCode={formValues.postalCode}
              phoneNumber={formValues.phoneNumber}
              claimUpdateDate={formValues.claimUpdateDate}
              basicSearchPayload={formValues}
              setShowBasicSearch={setShowBasicSearch}
              handleSubmit={handleSubmit}
            />
          )}
        </div>
      </div>

      {isPreviewLoading ? (
        <div style={{ minHeight: "300px" }}>
          <MotifProgressLoader show variant="default">
            {t("pages.configuration.scheme.lodaingIteamsMessage")}{" "}
          </MotifProgressLoader>
        </div>
      ) : previewError ? (
        <MotifErrorMessage>
          {t("global.globalNotifications.wentWrong")}
        </MotifErrorMessage>
      ) : previewData ? (
        previewData.length > 0 ? (
          <Claim previewData={previewData} />
        ) : (
          <MotifErrorMessage>
            {t("pages.configuration.scheme.noClaimFound")}
          </MotifErrorMessage>
        )
      ) : null}
    </>
  );
};
export default SchemePreview;
